import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Separator } from "../components/ui/separator";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  UtensilsCrossed,
  Plus,
  Edit,
  Trash2,
  Save,
  Loader2,
  ChefHat,
  Salad,
  Coffee,
  IceCream,
  Wine,
  AlertTriangle,
  Leaf,
  WheatOff,
  Eye,
  Download,
  Sparkles,
  X,
  Check,
  Euro,
  Upload,
  ImageIcon,
  Printer,
  FileText,
} from "lucide-react";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Import refactored components
import { 
  CATEGORIES, 
  ALLERGENES, 
  AVAILABLE_BACKGROUNDS, 
  TRANSLATIONS, 
  ALLERGENE_TRANSLATIONS,
  FTU_LOGO_URL
} from "../components/menu/constants";
import { TemplateSelector } from "../components/menu/TemplateSelector";
import {
  ClassicTemplate,
  ThaiTemplate,
  ItalianTemplate,
  MexicanTemplate,
  ModernTemplate,
} from "../components/menu/templates";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Note: CATEGORIES, ALLERGENES, AVAILABLE_BACKGROUNDS, TRANSLATIONS, ALLERGENE_TRANSLATIONS
// are now imported from ../components/menu/constants

export default function MenuManagementPage() {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [activeTab, setActiveTab] = useState("dishes");
  const [backgroundInfo, setBackgroundInfo] = useState(null);
  
  // Admin: food truck selection
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [selectedFoodTruck, setSelectedFoodTruck] = useState(null);
  
  // Dialog states
  const [showDishDialog, setShowDishDialog] = useState(false);
  const [editingDish, setEditingDish] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Menu generator states
  const [selectedEntrees, setSelectedEntrees] = useState([]);
  const [selectedPlats, setSelectedPlats] = useState([]);
  const [selectedDesserts, setSelectedDesserts] = useState([]);
  const [selectedBoissons, setSelectedBoissons] = useState([]);
  const [menuTitle, setMenuTitle] = useState("Notre Carte");
  const [menuSubtitle, setMenuSubtitle] = useState("");
  const [formules, setFormules] = useState([
    { nom: "Menu Midi", description: "Entrée + Plat", prix: 15, items: ["entree", "plat"] },
    { nom: "Menu Complet", description: "Entrée + Plat + Dessert", prix: 20, items: ["entree", "plat", "dessert"] },
    { nom: "Menu Express", description: "Plat + Boisson", prix: 14, items: ["plat", "boisson"] },
  ]);
  const [showPrix, setShowPrix] = useState(true);
  const [showAllergenes, setShowAllergenes] = useState(true);
  const [generatedMenu, setGeneratedMenu] = useState(null);
  const [menuLanguage, setMenuLanguage] = useState("fr"); // "fr" | "en"
  const [translating, setTranslating] = useState(false);
  const [translatedDishes, setTranslatedDishes] = useState({});
  
  // Event pricing mode
  const [eventPricingMode, setEventPricingMode] = useState(false);
  const [priceAdjustmentType, setPriceAdjustmentType] = useState("percentage"); // "percentage" | "fixed"
  const [priceAdjustmentValue, setPriceAdjustmentValue] = useState(0);
  const [customPrices, setCustomPrices] = useState({}); // { dishId: newPrice }
  const [customFormulePrices, setCustomFormulePrices] = useState({}); // { formuleIdx: newPrice }
  
  // Background options
  const [backgroundMode, setBackgroundMode] = useState("auto"); // "auto" | "custom" | "select"
  const [customBackgroundUrl, setCustomBackgroundUrl] = useState(null);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  
  // Formule dialog
  const [showFormuleDialog, setShowFormuleDialog] = useState(false);
  
  // AI Description generation
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [editingFormule, setEditingFormule] = useState(null);
  
  // Photo upload
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Import/Export states
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importReplaceAll, setImportReplaceAll] = useState(false);
  
  // PDF/Print states
  const [pageOrientation, setPageOrientation] = useState("portrait"); // portrait or landscape
  const [exportingPDF, setExportingPDF] = useState(false);
  
  // Menu template style
  const [menuTemplate, setMenuTemplate] = useState("classic"); // "classic" | "visual-thai" | "visual-italian" | "visual-mexican" | "visual-modern"
  
  // Custom logo for menu
  const [customLogoUrl, setCustomLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  const menuRef = useRef(null);
  const printMenuRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [selectedFoodTruck]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Admin/Bureau: load food trucks list if not already loaded
      const canSelectFoodTruck = isAdmin() || user?.role === "bureau";
      if (canSelectFoodTruck && foodTrucks.length === 0) {
        const ftRes = await axios.get(`${API_URL}/api/food-trucks`, { headers });
        setFoodTrucks(ftRes.data.filter(ft => ft.statut === "actif"));
        // Auto-select first food truck if none selected
        if (!selectedFoodTruck && ftRes.data.length > 0) {
          const activeFts = ftRes.data.filter(ft => ft.statut === "actif");
          if (activeFts.length > 0) {
            setSelectedFoodTruck(activeFts[0].id);
            return; // Will trigger useEffect again with selected FT
          }
        }
      }
      
      // Build URL with food_truck_id for admin/bureau
      let dishesUrl = `${API_URL}/api/menus/dishes`;
      if (canSelectFoodTruck && selectedFoodTruck) {
        dishesUrl += `?food_truck_id=${selectedFoodTruck}`;
      }
      
      const [dishesRes, bgRes] = await Promise.all([
        axios.get(dishesUrl, { headers }),
        axios.get(`${API_URL}/api/menus/food-truck-background${canSelectFoodTruck && selectedFoodTruck ? `?food_truck_id=${selectedFoodTruck}` : ''}`, { headers })
      ]);
      
      setDishes(dishesRes.data);
      setBackgroundInfo(bgRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response?.status !== 404) {
        toast.error("Erreur lors du chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDish = async () => {
    if (!editingDish?.nom || !editingDish?.prix) {
      toast.error("Nom et prix obligatoires");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build URL with food_truck_id for admin/bureau
      let url = `${API_URL}/api/menus/dishes`;
      const canSelectFoodTruck = isAdmin() || user?.role === "bureau";
      if (canSelectFoodTruck && selectedFoodTruck && !editingDish.id) {
        url += `?food_truck_id=${selectedFoodTruck}`;
      }
      
      // Convert text fields to arrays before saving
      const dishToSave = {
        ...editingDish,
        ingredients: editingDish.ingredientsText !== undefined
          ? editingDish.ingredientsText.split(",").map(s => s.trim()).filter(Boolean)
          : (editingDish.ingredients || []),
        accompagnements: editingDish.accompagnementsText !== undefined
          ? editingDish.accompagnementsText.split(",").map(s => s.trim()).filter(Boolean)
          : (editingDish.accompagnements || [])
      };
      // Remove temporary text fields
      delete dishToSave.ingredientsText;
      delete dishToSave.accompagnementsText;
      
      if (editingDish.id) {
        await axios.put(`${API_URL}/api/menus/dishes/${editingDish.id}`, dishToSave, { headers });
        toast.success("Plat mis à jour");
      } else {
        await axios.post(url, dishToSave, { headers });
        toast.success("Plat créé");
      }
      
      setShowDishDialog(false);
      setEditingDish(null);
      loadData();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDish = async (dishId) => {
    if (!window.confirm("Supprimer ce plat ?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/menus/dishes/${dishId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Plat supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // ==================== EXPORT / IMPORT ====================
  
  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem("token");
      let url = `${API_URL}/api/menus/dishes/export`;
      if (selectedFoodTruck) {
        url += `?food_truck_id=${selectedFoodTruck}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.data && response.data.data.length > 0) {
        // Convert to CSV string
        const columns = response.data.columns;
        const rows = response.data.data;
        
        let csvContent = columns.join(";") + "\n";
        rows.forEach(row => {
          csvContent += columns.map(col => {
            let val = row[col] || "";
            // Escape quotes and wrap in quotes if contains separator
            if (String(val).includes(";") || String(val).includes("\n")) {
              val = `"${String(val).replace(/"/g, '""')}"`;
            }
            return val;
          }).join(";") + "\n";
        });
        
        // Create blob and download
        const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `menu_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        toast.success(`${response.data.count} plat(s) exporté(s)`);
      } else {
        toast.info("Aucun plat à exporter");
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Erreur lors de l'export");
    }
  };
  
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/menus/dishes/template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const columns = response.data.columns;
      const template = response.data.template;
      
      // Create CSV with template rows
      let csvContent = columns.join(";") + "\n";
      template.forEach(row => {
        csvContent += columns.map(col => row[col] || "").join(";") + "\n";
      });
      
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "modele_import_menu.csv";
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.success("Modèle téléchargé");
    } catch (error) {
      toast.error("Erreur lors du téléchargement du modèle");
    }
  };
  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter(line => line.trim());
        
        if (lines.length < 2) {
          toast.error("Le fichier doit contenir au moins une ligne d'en-tête et une ligne de données");
          return;
        }
        
        // Parse CSV (support both ; and , as separators)
        const separator = lines[0].includes(";") ? ";" : ",";
        const headers = lines[0].split(separator).map(h => h.trim().toLowerCase().replace(/"/g, ""));
        
        const data = [];
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = lines[i].split(separator).map(v => v.trim().replace(/^"|"$/g, ""));
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          
          if (row.nom) { // Only add rows with a name
            data.push(row);
          }
        }
        
        setImportData(data);
        setShowImportDialog(true);
        toast.info(`${data.length} plat(s) détecté(s)`);
      } catch (error) {
        console.error("Parse error:", error);
        toast.error("Erreur lors de la lecture du fichier");
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = "";
  };
  
  const handleImportConfirm = async () => {
    if (importData.length === 0) {
      toast.error("Aucune donnée à importer");
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem("token");
      let url = `${API_URL}/api/menus/dishes/import?replace_all=${importReplaceAll}`;
      if (selectedFoodTruck) {
        url += `&food_truck_id=${selectedFoodTruck}`;
      }
      
      const response = await axios.post(url, importData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(err => toast.warning(err));
      }
      
      setShowImportDialog(false);
      setImportData([]);
      setImportReplaceAll(false);
      loadData();
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  // Generate AI description for a dish
  const handleGenerateDescription = async () => {
    if (!editingDish?.nom || editingDish.nom.trim().length < 2) {
      toast.error("Entrez d'abord le nom du plat");
      return;
    }

    setGeneratingDescription(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/menus/generate-description`,
        { 
          nom: editingDish.nom,
          categorie: editingDish.categorie || null,
          include_allergenes: true
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.description) {
        const updates = { 
          ...editingDish, 
          description: response.data.description 
        };
        
        // Also update allergenes if returned
        if (response.data.allergenes && response.data.allergenes.length > 0) {
          updates.allergenes = response.data.allergenes;
          toast.success(`Description + ${response.data.allergenes.length} allergène(s) générés !`);
        } else {
          toast.success("Description générée !");
        }
        
        setEditingDish(updates);
      }
    } catch (error) {
      console.error("Generate description error:", error);
      toast.error("Erreur lors de la génération");
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image est trop volumineuse (max 5MB)");
      return;
    }

    // Check file type
    if (!file.type.startsWith("image/")) {
      toast.error("Seules les images sont acceptées");
      return;
    }

    setUploadingPhoto(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const imageUrl = `${API_URL}${response.data.url}`;
      setEditingDish({ ...editingDish, image_url: imageUrl });
      toast.success("Photo uploadée");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erreur lors de l'upload de la photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("L'image est trop volumineuse (max 10MB)");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Seules les images sont acceptées");
      return;
    }

    setUploadingBackground(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const imageUrl = `${API_URL}${response.data.url}`;
      setCustomBackgroundUrl(imageUrl);
      setBackgroundMode("custom");
      toast.success("Fond personnalisé uploadé");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erreur lors de l'upload du fond");
    } finally {
      setUploadingBackground(false);
    }
  };

  // Handle logo upload for menu
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Le logo est trop volumineux (max 5MB)");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Seules les images sont acceptées");
      return;
    }

    setUploadingLogo(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API_URL}/api/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const imageUrl = `${API_URL}${response.data.url}`;
      setCustomLogoUrl(imageUrl);
      toast.success("Logo uploadé avec succès !");
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error("Erreur lors de l'upload du logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Get the current background URL based on mode
  const getCurrentBackgroundUrl = () => {
    if (backgroundMode === "custom" && customBackgroundUrl) {
      return customBackgroundUrl;
    }
    if (backgroundMode === "select" && customBackgroundUrl) {
      return customBackgroundUrl;
    }
    return backgroundInfo?.background_url || AVAILABLE_BACKGROUNDS.find(b => b.id === "default")?.url;
  };

  // Calculate adjusted price for event mode
  const getAdjustedPrice = (originalPrice, dishId) => {
    // If custom price is set for this dish, use it
    if (customPrices[dishId] !== undefined) {
      return customPrices[dishId];
    }
    
    // If event pricing mode is off, return original
    if (!eventPricingMode) {
      return originalPrice;
    }
    
    // Apply global adjustment
    if (priceAdjustmentType === "percentage") {
      return originalPrice * (1 + priceAdjustmentValue / 100);
    } else {
      return originalPrice + priceAdjustmentValue;
    }
  };

  // Update custom price for a dish
  const updateCustomPrice = (dishId, newPrice) => {
    setCustomPrices(prev => ({
      ...prev,
      [dishId]: parseFloat(newPrice) || 0
    }));
  };

  // Reset custom price to use global adjustment
  const resetCustomPrice = (dishId) => {
    setCustomPrices(prev => {
      const updated = { ...prev };
      delete updated[dishId];
      return updated;
    });
  };

  // Apply global adjustment to all selected dishes
  const applyGlobalAdjustment = () => {
    const allSelected = [...selectedEntrees, ...selectedPlats, ...selectedDesserts, ...selectedBoissons];
    const newCustomPrices = {};
    
    allSelected.forEach(dishId => {
      const dish = dishes.find(d => d.id === dishId);
      if (dish) {
        if (priceAdjustmentType === "percentage") {
          newCustomPrices[dishId] = dish.prix * (1 + priceAdjustmentValue / 100);
        } else {
          newCustomPrices[dishId] = dish.prix + priceAdjustmentValue;
        }
      }
    });
    
    // Also apply to formules
    const newFormulePrices = {};
    formules.forEach((formule, idx) => {
      if (priceAdjustmentType === "percentage") {
        newFormulePrices[idx] = formule.prix * (1 + priceAdjustmentValue / 100);
      } else {
        newFormulePrices[idx] = formule.prix + priceAdjustmentValue;
      }
    });
    
    setCustomPrices(newCustomPrices);
    setCustomFormulePrices(newFormulePrices);
    toast.success("Prix ajustés pour tous les plats et formules");
  };

  // Reset all custom prices
  const resetAllPrices = () => {
    setCustomPrices({});
    setCustomFormulePrices({});
    toast.success("Prix remis aux valeurs originales");
  };

  // Update custom formule price
  const updateCustomFormulePrice = (idx, newPrice) => {
    setCustomFormulePrices(prev => ({
      ...prev,
      [idx]: parseFloat(newPrice) || 0
    }));
  };

  // Reset custom formule price
  const resetCustomFormulePrice = (idx) => {
    setCustomFormulePrices(prev => {
      const updated = { ...prev };
      delete updated[idx];
      return updated;
    });
  };

  // Translate dishes to target language
  const translateDishes = async (dishesToTranslate, targetLang) => {
    if (targetLang === "fr" || dishesToTranslate.length === 0) {
      return dishesToTranslate;
    }
    
    // Check cache first
    const cacheKey = dishesToTranslate.map(d => d.id).join(",") + "_" + targetLang;
    if (translatedDishes[cacheKey]) {
      return translatedDishes[cacheKey];
    }
    
    setTranslating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}/api/menus/translate`,
        {
          dishes: dishesToTranslate,
          target_language: targetLang
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.status === "success" || response.data.status === "partial") {
        // Cache the translation
        setTranslatedDishes(prev => ({
          ...prev,
          [cacheKey]: response.data.dishes
        }));
        return response.data.dishes;
      }
      return dishesToTranslate;
    } catch (error) {
      console.error("Translation error:", error);
      toast.error("Erreur de traduction, affichage en français");
      return dishesToTranslate;
    } finally {
      setTranslating(false);
    }
  };

  // Handle language change
  const handleLanguageChange = async (newLang) => {
    setMenuLanguage(newLang);
    
    // If switching to English and we have a generated menu, translate it
    if (newLang === "en" && generatedMenu) {
      const allDishes = generatedMenu.sections?.flatMap(s => s.plats) || [];
      if (allDishes.length > 0) {
        const translated = await translateDishes(allDishes, "en");
        // Update the generated menu with translated dishes
        setGeneratedMenu(prev => ({
          ...prev,
          sections: prev.sections.map(section => ({
            ...section,
            plats: section.plats.map(plat => {
              const translatedPlat = translated.find(t => t.id === plat.id);
              return translatedPlat || plat;
            })
          }))
        }));
      }
    }
  };

  const handleGenerateMenu = async () => {
    // Check if admin/bureau has selected a food truck
    const canSelectFoodTruck = isAdmin() || user?.role === "bureau";
    if (canSelectFoodTruck && !selectedFoodTruck) {
      toast.error("Veuillez sélectionner un food truck");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      
      // Build URL with food_truck_id for admin/bureau
      let url = `${API_URL}/api/menus/generate`;
      if (canSelectFoodTruck && selectedFoodTruck) {
        url += `?food_truck_id=${selectedFoodTruck}`;
      }
      
      // Apply custom formule prices if event pricing mode is enabled
      const formulesWithAdjustedPrices = eventPricingMode && Object.keys(customFormulePrices).length > 0
        ? formules.map((formule, idx) => ({
            ...formule,
            prix: customFormulePrices[idx] !== undefined ? customFormulePrices[idx] : formule.prix,
            original_prix: customFormulePrices[idx] !== undefined ? formule.prix : undefined
          }))
        : formules;
      
      const response = await axios.post(
        url,
        {
          titre: menuTitle,
          sous_titre: menuSubtitle,
          entrees: selectedEntrees,
          plats: selectedPlats,
          desserts: selectedDesserts,
          boissons: selectedBoissons,
          formules: formulesWithAdjustedPrices,
          show_prix: showPrix,
          show_allergenes: showAllergenes,
          style: "classic",
          custom_background_url: backgroundMode !== "auto" ? getCurrentBackgroundUrl() : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Override background if custom
      let menuData = response.data;
      if (backgroundMode !== "auto" && getCurrentBackgroundUrl()) {
        menuData.background_url = getCurrentBackgroundUrl();
      }
      
      // Translate if English is selected
      if (menuLanguage === "en" && menuData.sections) {
        const allDishes = menuData.sections.flatMap(s => s.plats).filter(Boolean) || [];
        if (allDishes.length > 0) {
          try {
            const translated = await translateDishes(allDishes, "en");
            if (translated && translated.length > 0) {
              // Create a map for faster lookup
              const translationMap = {};
              translated.forEach(t => {
                if (t && t.id) {
                  translationMap[t.id] = t;
                }
              });
              
              menuData = {
                ...menuData,
                sections: menuData.sections.map(section => ({
                  ...section,
                  plats: section.plats.map(plat => {
                    const translatedPlat = translationMap[plat.id];
                    if (translatedPlat) {
                      return {
                        ...plat,
                        nom: translatedPlat.nom || plat.nom,
                        description: translatedPlat.description || plat.description
                      };
                    }
                    return plat;
                  })
                }))
              };
            }
          } catch (transError) {
            console.error("Translation failed:", transError);
            toast.error("Erreur de traduction, affichage en français");
          }
        }
      }
      
      // Apply custom prices if event pricing mode is enabled
      if (eventPricingMode && Object.keys(customPrices).length > 0) {
        menuData = {
          ...menuData,
          sections: menuData.sections.map(section => ({
            ...section,
            plats: section.plats.map(plat => {
              if (customPrices[plat.id] !== undefined) {
                return {
                  ...plat,
                  prix: customPrices[plat.id],
                  original_prix: plat.prix
                };
              }
              return plat;
            })
          }))
        };
      }
      
      setGeneratedMenu(menuData);
      setShowPreview(true);
    } catch (error) {
      console.error("Generate error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadMenu = async () => {
    if (!menuRef.current) return;
    
    try {
      const canvas = await html2canvas(menuRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null
      });
      
      const link = document.createElement('a');
      link.download = `menu_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success("Menu téléchargé");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  // Export PDF function
  const handleExportPDF = async () => {
    if (!printMenuRef.current) return;
    
    setExportingPDF(true);
    try {
      // A4 dimensions in mm
      const isLandscape = pageOrientation === "landscape";
      const pageWidth = isLandscape ? 297 : 210;
      const pageHeight = isLandscape ? 210 : 297;
      
      // Generate canvas from the print-optimized menu
      const canvas = await html2canvas(printMenuRef.current, {
        scale: 3, // Higher scale for better quality
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        windowWidth: isLandscape ? 1123 : 794, // A4 at 96 DPI
        windowHeight: isLandscape ? 794 : 1123
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: pageOrientation,
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate dimensions to fit the page with margins
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);
      
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(contentWidth / imgWidth, contentHeight / imgHeight);
      
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      
      // Center the image on the page
      const x = margin + (contentWidth - scaledWidth) / 2;
      const y = margin + (contentHeight - scaledHeight) / 2;
      
      pdf.addImage(imgData, 'JPEG', x, y, scaledWidth, scaledHeight);
      
      // Get food truck name for filename
      const ftName = generatedMenu?.food_truck?.nom || "menu";
      const safeName = ftName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      
      pdf.save(`menu_${safeName}_${pageOrientation}.pdf`);
      toast.success("PDF exporté avec succès !");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Erreur lors de l'export PDF");
    } finally {
      setExportingPDF(false);
    }
  };

  // Print function  
  const handlePrint = () => {
    window.print();
  };

  const handleAddFormule = () => {
    setEditingFormule({
      nom: "",
      description: "",
      prix: 0,
      includes_entree: false,
      includes_plat: true,
      includes_dessert: false,
      includes_boisson: false
    });
    setShowFormuleDialog(true);
  };

  const handleSaveFormule = () => {
    if (!editingFormule?.nom || !editingFormule?.prix) {
      toast.error("Nom et prix obligatoires");
      return;
    }
    
    const items = [];
    if (editingFormule.includes_entree) items.push("entree");
    if (editingFormule.includes_plat) items.push("plat");
    if (editingFormule.includes_dessert) items.push("dessert");
    if (editingFormule.includes_boisson) items.push("boisson");
    
    const newFormule = {
      nom: editingFormule.nom,
      description: editingFormule.description,
      prix: editingFormule.prix,
      items: items
    };
    
    setFormules([...formules, newFormule]);
    setShowFormuleDialog(false);
    setEditingFormule(null);
    toast.success("Formule ajoutée");
  };

  const toggleDishSelection = (dishId, category) => {
    const setters = {
      entree: setSelectedEntrees,
      plat: setSelectedPlats,
      dessert: setSelectedDesserts,
      boisson: setSelectedBoissons
    };
    
    const current = {
      entree: selectedEntrees,
      plat: selectedPlats,
      dessert: selectedDesserts,
      boisson: selectedBoissons
    };
    
    const setter = setters[category];
    const list = current[category];
    
    if (list.includes(dishId)) {
      setter(list.filter(id => id !== dishId));
    } else {
      setter([...list, dishId]);
    }
  };

  // Group dishes by category
  const dishesByCategory = dishes.reduce((acc, dish) => {
    const cat = dish.categorie || "plat";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(dish);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="menu-management-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-secondary flex items-center gap-2">
            <UtensilsCrossed className="w-6 h-6 text-primary" />
            Gestion du Menu
          </h1>
          <p className="text-muted-foreground text-sm">Gérez vos plats et générez des menus</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {/* Admin/Bureau: Food Truck Selector */}
          {(isAdmin() || user?.role === "bureau") && foodTrucks.length > 0 && (
            <Select value={selectedFoodTruck || ""} onValueChange={setSelectedFoodTruck}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Sélectionner un food truck" />
              </SelectTrigger>
              <SelectContent>
                {foodTrucks.map((ft) => (
                  <SelectItem key={ft.id} value={ft.id}>
                    {ft.nom_food_truck}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button className="flex-1 sm:flex-none" onClick={() => {
            setEditingDish({
              nom: "",
              description: "",
              prix: 0,
              categorie: "plat",
              ingredients: [],
              allergenes: [],
              accompagnements: [],
              vegetarien: false,
              vegan: false,
              sans_gluten: false,
              disponible: true
            });
            setShowDishDialog(true);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            <span className="hidden xs:inline">Nouveau</span> Plat
          </Button>
          
          {/* Import/Export Buttons */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleExportCSV}
              className="gap-1"
              title="Exporter CSV"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exporter</span>
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="gap-1"
              title="Importer CSV"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Importer</span>
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const count = (dishesByCategory[cat.id] || []).length;
          return (
            <Card key={cat.id} className="card-hover">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${cat.color} flex items-center justify-center`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-muted-foreground">{cat.label}s</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="dishes" className="flex items-center gap-2">
            <ChefHat className="w-4 h-4" />
            Mes Plats
          </TabsTrigger>
          <TabsTrigger value="generator" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Générateur de Menu
          </TabsTrigger>
        </TabsList>

        {/* Dishes Tab */}
        <TabsContent value="dishes" className="space-y-4">
          {dishes.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <UtensilsCrossed className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-semibold mb-2">Aucun plat enregistré</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Commencez par ajouter vos entrées, plats et desserts
                </p>
                <Button onClick={() => {
                  setEditingDish({
                    nom: "", description: "", prix: 0, categorie: "plat",
                    ingredients: [], allergenes: [], accompagnements: [],
                    vegetarien: false, vegan: false, sans_gluten: false, disponible: true
                  });
                  setShowDishDialog(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter mon premier plat
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {CATEGORIES.map((cat) => {
                const categoryDishes = dishesByCategory[cat.id] || [];
                if (categoryDishes.length === 0) return null;
                
                const Icon = cat.icon;
                return (
                  <Card key={cat.id}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Icon className="w-5 h-5" />
                        {cat.label}s ({categoryDishes.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {categoryDishes.map((dish) => (
                          <div
                            key={dish.id}
                            className={`p-4 border rounded-lg hover:shadow-md transition-shadow ${
                              !dish.disponible ? "opacity-50" : ""
                            }`}
                          >
                            {/* Photo du plat */}
                            {dish.image_url && (
                              <div className="mb-3 -mx-4 -mt-4">
                                <img
                                  src={dish.image_url}
                                  alt={dish.nom}
                                  className="w-full h-32 object-cover rounded-t-lg"
                                />
                              </div>
                            )}
                            
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-secondary">{dish.nom}</h4>
                                <p className="text-lg font-bold text-primary">{dish.prix.toFixed(2)}€</p>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setEditingDish(dish);
                                    setShowDishDialog(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleDeleteDish(dish.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {dish.description && (
                              <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                                {dish.description}
                              </p>
                            )}
                            
                            <div className="flex flex-wrap gap-1 mb-2">
                              {dish.vegetarien && (
                                <Badge variant="outline" className="text-green-600 border-green-300 text-xs">
                                  <Leaf className="w-3 h-3 mr-1" /> Végé
                                </Badge>
                              )}
                              {dish.vegan && (
                                <Badge variant="outline" className="text-green-700 border-green-400 text-xs">
                                  <Leaf className="w-3 h-3 mr-1" /> Vegan
                                </Badge>
                              )}
                              {dish.sans_gluten && (
                                <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                                  <WheatOff className="w-3 h-3 mr-1" /> Sans gluten
                                </Badge>
                              )}
                            </div>
                            
                            {dish.allergenes?.length > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <AlertTriangle className="w-3 h-3 text-amber-500" />
                                {dish.allergenes.slice(0, 3).join(", ")}
                                {dish.allergenes.length > 3 && `...`}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Menu Generator Tab */}
        <TabsContent value="generator" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Selection */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Configuration du Menu</CardTitle>
                  <CardDescription>Sélectionnez les plats à inclure dans votre menu</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Template Selection */}
                  <div>
                    <Label className="mb-2 block">Style du menu</Label>
                    <div className="grid grid-cols-5 gap-2">
                      <button
                        type="button"
                        onClick={() => setMenuTemplate("classic")}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          menuTemplate === "classic"
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="text-2xl mb-1">📋</div>
                        <div className="text-xs font-medium">Classique</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMenuTemplate("visual-thai")}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          menuTemplate === "visual-thai"
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="text-2xl mb-1">🍜</div>
                        <div className="text-xs font-medium">Thaï</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMenuTemplate("visual-italian")}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          menuTemplate === "visual-italian"
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="text-2xl mb-1">🍕</div>
                        <div className="text-xs font-medium">Italien</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMenuTemplate("visual-mexican")}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          menuTemplate === "visual-mexican"
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="text-2xl mb-1">🌮</div>
                        <div className="text-xs font-medium">Mexicain</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setMenuTemplate("visual-modern")}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          menuTemplate === "visual-modern"
                            ? "border-primary bg-primary/5"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="text-2xl mb-1">✨</div>
                        <div className="text-xs font-medium">Moderne</div>
                      </button>
                    </div>
                  </div>
                  
                  {/* Logo personnalisé - visible uniquement pour les templates visuels */}
                  {menuTemplate !== "classic" && (
                    <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0">
                        {customLogoUrl ? (
                          <img 
                            src={customLogoUrl} 
                            alt="Logo personnalisé" 
                            className="w-16 h-16 object-contain rounded-lg border bg-white"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-2xl">
                            🏪
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <Label className="text-sm font-medium">Logo personnalisé</Label>
                        <p className="text-xs text-muted-foreground mb-2">
                          Uploadez le logo de votre food truck pour le menu
                        </p>
                        <div className="flex gap-2">
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleLogoUpload}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => logoInputRef.current?.click()}
                            disabled={uploadingLogo}
                          >
                            {uploadingLogo ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            {customLogoUrl ? "Changer" : "Uploader"}
                          </Button>
                          {customLogoUrl && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setCustomLogoUrl(null)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Separator />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Titre du menu</Label>
                      <Input
                        value={menuTitle}
                        onChange={(e) => setMenuTitle(e.target.value)}
                        placeholder="Notre Carte"
                      />
                    </div>
                    <div>
                      <Label>Sous-titre (optionnel)</Label>
                      <Input
                        value={menuSubtitle}
                        onChange={(e) => setMenuSubtitle(e.target.value)}
                        placeholder="Cuisine traditionnelle"
                      />
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="showPrix"
                        checked={showPrix}
                        onCheckedChange={setShowPrix}
                      />
                      <Label htmlFor="showPrix" className="text-sm">Afficher les prix</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="showAllergenes"
                        checked={showAllergenes}
                        onCheckedChange={setShowAllergenes}
                      />
                      <Label htmlFor="showAllergenes" className="text-sm">Afficher les allergènes</Label>
                    </div>
                      <div className="flex items-center gap-2 ml-auto">
                      <Label className="text-sm">Langue :</Label>
                      <div className="flex border rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => handleLanguageChange("fr")}
                          disabled={translating}
                          className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                            menuLanguage === "fr"
                              ? "bg-primary text-white"
                              : "bg-white text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          🇫🇷 FR
                        </button>
                        <button
                          type="button"
                          onClick={() => handleLanguageChange("en")}
                          disabled={translating}
                          className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                            menuLanguage === "en"
                              ? "bg-primary text-white"
                              : "bg-white text-gray-600 hover:bg-gray-50"
                          } ${translating ? "opacity-50" : ""}`}
                        >
                          {translating ? "..." : "🇬🇧 EN"}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  {/* Entrées Selection */}
                  {(dishesByCategory.entree || []).length > 0 && (
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <Salad className="w-4 h-4 text-green-600" />
                        Entrées
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {(dishesByCategory.entree || []).map((dish) => (
                          <div
                            key={dish.id}
                            onClick={() => toggleDishSelection(dish.id, "entree")}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${
                              selectedEntrees.includes(dish.id)
                                ? "border-green-500 bg-green-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div 
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                  selectedEntrees.includes(dish.id) 
                                    ? "bg-green-500 border-green-500" 
                                    : "border-gray-300"
                                }`}
                              >
                                {selectedEntrees.includes(dish.id) && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <span className="text-sm font-medium">{dish.nom}</span>
                            </div>
                            <p className="text-xs text-muted-foreground ml-6">{dish.prix.toFixed(2)}€</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Plats Selection */}
                  {(dishesByCategory.plat || []).length > 0 && (
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <ChefHat className="w-4 h-4 text-orange-600" />
                        Plats
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {(dishesByCategory.plat || []).map((dish) => (
                          <div
                            key={dish.id}
                            onClick={() => toggleDishSelection(dish.id, "plat")}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${
                              selectedPlats.includes(dish.id)
                                ? "border-orange-500 bg-orange-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div 
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                  selectedPlats.includes(dish.id) 
                                    ? "bg-orange-500 border-orange-500" 
                                    : "border-gray-300"
                                }`}
                              >
                                {selectedPlats.includes(dish.id) && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <span className="text-sm font-medium">{dish.nom}</span>
                            </div>
                            <p className="text-xs text-muted-foreground ml-6">{dish.prix.toFixed(2)}€</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Desserts Selection */}
                  {(dishesByCategory.dessert || []).length > 0 && (
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <IceCream className="w-4 h-4 text-pink-600" />
                        Desserts
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {(dishesByCategory.dessert || []).map((dish) => (
                          <div
                            key={dish.id}
                            onClick={() => toggleDishSelection(dish.id, "dessert")}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${
                              selectedDesserts.includes(dish.id)
                                ? "border-pink-500 bg-pink-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div 
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                  selectedDesserts.includes(dish.id) 
                                    ? "bg-pink-500 border-pink-500" 
                                    : "border-gray-300"
                                }`}
                              >
                                {selectedDesserts.includes(dish.id) && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <span className="text-sm font-medium">{dish.nom}</span>
                            </div>
                            <p className="text-xs text-muted-foreground ml-6">{dish.prix.toFixed(2)}€</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Boissons Selection */}
                  {(dishesByCategory.boisson || []).length > 0 && (
                    <div>
                      <Label className="flex items-center gap-2 mb-2">
                        <Coffee className="w-4 h-4 text-blue-600" />
                        Boissons
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {(dishesByCategory.boisson || []).map((dish) => (
                          <div
                            key={dish.id}
                            onClick={() => toggleDishSelection(dish.id, "boisson")}
                            className={`p-3 border rounded-lg cursor-pointer transition-all ${
                              selectedBoissons.includes(dish.id)
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div 
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                  selectedBoissons.includes(dish.id) 
                                    ? "bg-blue-500 border-blue-500" 
                                    : "border-gray-300"
                                }`}
                              >
                                {selectedBoissons.includes(dish.id) && (
                                  <Check className="w-3 h-3 text-white" />
                                )}
                              </div>
                              <span className="text-sm font-medium">{dish.nom}</span>
                            </div>
                            <p className="text-xs text-muted-foreground ml-6">{dish.prix.toFixed(2)}€</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Right: Formules & Actions */}
            <div className="space-y-4">
              {/* Event Pricing Mode */}
              <Card className="border-2 border-dashed border-orange-300 bg-orange-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Euro className="w-5 h-5 text-orange-600" />
                      Prix Événement
                    </span>
                    <Switch
                      checked={eventPricingMode}
                      onCheckedChange={setEventPricingMode}
                    />
                  </CardTitle>
                  <CardDescription>
                    Ajustez les prix pour un événement spécial
                  </CardDescription>
                </CardHeader>
                {eventPricingMode && (
                  <CardContent className="space-y-4">
                    {/* Global adjustment */}
                    <div className="space-y-3 p-3 bg-white rounded-lg border">
                      <Label className="text-sm font-semibold">Ajustement global</Label>
                      <div className="flex gap-2">
                        <Select value={priceAdjustmentType} onValueChange={setPriceAdjustmentType}>
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Pourcentage %</SelectItem>
                            <SelectItem value="fixed">Montant fixe €</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={priceAdjustmentValue}
                          onChange={(e) => setPriceAdjustmentValue(parseFloat(e.target.value) || 0)}
                          className="w-24"
                          placeholder={priceAdjustmentType === "percentage" ? "%" : "€"}
                        />
                        <Button size="sm" onClick={applyGlobalAdjustment} className="bg-orange-600 hover:bg-orange-700">
                          Appliquer
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {priceAdjustmentType === "percentage" 
                          ? `Ex: +10% = prix × 1.10`
                          : `Ex: +2€ = prix + 2€`
                        }
                      </p>
                    </div>
                    
                    {/* Individual price adjustments */}
                    {(selectedEntrees.length > 0 || selectedPlats.length > 0 || selectedDesserts.length > 0 || selectedBoissons.length > 0) && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm font-semibold">Prix individuels</Label>
                          <Button size="sm" variant="ghost" className="text-xs h-7" onClick={resetAllPrices}>
                            Réinitialiser
                          </Button>
                        </div>
                        <ScrollArea className="h-[200px] pr-2">
                          <div className="space-y-2">
                            {[...selectedEntrees, ...selectedPlats, ...selectedDesserts, ...selectedBoissons].map(dishId => {
                              const dish = dishes.find(d => d.id === dishId);
                              if (!dish) return null;
                              const currentPrice = customPrices[dishId] !== undefined ? customPrices[dishId] : dish.prix;
                              const isModified = customPrices[dishId] !== undefined;
                              
                              return (
                                <div key={dishId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium truncate">{dish.nom}</p>
                                    <p className="text-xs text-muted-foreground">
                                      Original: {dish.prix.toFixed(2)}€
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="number"
                                      step="0.50"
                                      value={currentPrice.toFixed(2)}
                                      onChange={(e) => updateCustomPrice(dishId, e.target.value)}
                                      className={`w-20 h-8 text-right ${isModified ? 'border-orange-500 bg-orange-50' : ''}`}
                                    />
                                    <span className="text-sm">€</span>
                                    {isModified && (
                                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => resetCustomPrice(dishId)}>
                                        <X className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </ScrollArea>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
              
              {/* Formules */}
              <Card className={eventPricingMode ? "border-2 border-dashed border-orange-300" : ""}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Wine className="w-5 h-5" />
                      Formules
                      {eventPricingMode && Object.keys(customFormulePrices).length > 0 && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 text-xs">
                          Prix modifiés
                        </Badge>
                      )}
                    </span>
                    <Button size="sm" variant="outline" onClick={handleAddFormule}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {formules.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Aucune formule définie
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {formules.map((formule, idx) => {
                        const currentPrice = customFormulePrices[idx] !== undefined ? customFormulePrices[idx] : formule.prix;
                        const isModified = customFormulePrices[idx] !== undefined;
                        
                        return (
                          <div key={idx} className={`p-3 rounded-lg ${isModified ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'}`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-semibold">{formule.nom}</p>
                                <p className="text-sm text-muted-foreground">
                                  {formule.items.map(i => 
                                    i === "entree" ? "Entrée" : 
                                    i === "plat" ? "Plat" : 
                                    i === "dessert" ? "Dessert" : "Boisson"
                                  ).join(" + ")}
                                </p>
                                {isModified && (
                                  <p className="text-xs text-orange-600 mt-1">
                                    Original: {formule.prix.toFixed(2)}€
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {eventPricingMode ? (
                                  <>
                                    <Input
                                      type="number"
                                      step="0.50"
                                      value={currentPrice.toFixed(2)}
                                      onChange={(e) => updateCustomFormulePrice(idx, e.target.value)}
                                      className={`w-20 h-8 text-right ${isModified ? 'border-orange-500 bg-white' : ''}`}
                                    />
                                    <span className="text-sm font-medium">€</span>
                                    {isModified && (
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="h-8 w-8 p-0" 
                                        onClick={() => resetCustomFormulePrice(idx)}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <span className="font-bold text-primary">{formule.prix}€</span>
                                )}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500"
                                  onClick={() => setFormules(formules.filter((_, i) => i !== idx))}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Actions */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    className="w-full"
                    onClick={handleGenerateMenu}
                    disabled={saving || translating || (selectedEntrees.length === 0 && selectedPlats.length === 0 && selectedDesserts.length === 0)}
                  >
                    {saving || translating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    {translating ? "Traduction..." : saving ? "Génération..." : "Aperçu du Menu"}
                  </Button>
                  
                  {menuLanguage === "en" && (
                    <p className="text-xs text-blue-600 text-center flex items-center justify-center gap-1">
                      <span>🇬🇧</span> Le menu sera traduit automatiquement en anglais
                    </p>
                  )}
                  
                  <p className="text-xs text-muted-foreground text-center">
                    Sélectionnez au moins un plat pour générer le menu
                  </p>
                </CardContent>
              </Card>
              
              {/* Background Selection */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Fond du Menu
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Mode Selection */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={backgroundMode === "auto" ? "default" : "outline"}
                      onClick={() => setBackgroundMode("auto")}
                      className="flex-1"
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      Auto
                    </Button>
                    <Button
                      size="sm"
                      variant={backgroundMode === "select" ? "default" : "outline"}
                      onClick={() => setBackgroundMode("select")}
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Choisir
                    </Button>
                    <Button
                      size="sm"
                      variant={backgroundMode === "custom" ? "default" : "outline"}
                      onClick={() => setBackgroundMode("custom")}
                      className="flex-1"
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                  
                  {/* Auto Mode - Show detected country */}
                  {backgroundMode === "auto" && backgroundInfo && (
                    <div
                      className="h-24 bg-cover bg-center relative rounded-lg overflow-hidden"
                      style={{ backgroundImage: `url(${backgroundInfo.background_url})` }}
                    >
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <p className="text-white font-semibold text-sm">
                          Détecté: {backgroundInfo.country?.toUpperCase() || "DEFAULT"}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Select Mode - Grid of backgrounds */}
                  {backgroundMode === "select" && (
                    <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                      {AVAILABLE_BACKGROUNDS.map((bg) => (
                        <div
                          key={bg.id}
                          onClick={() => setCustomBackgroundUrl(bg.url)}
                          className={`relative h-16 bg-cover bg-center rounded cursor-pointer transition-all ${
                            customBackgroundUrl === bg.url 
                              ? "ring-2 ring-primary ring-offset-2" 
                              : "hover:opacity-80"
                          }`}
                          style={{ backgroundImage: `url(${bg.url})` }}
                        >
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded">
                            <span className="text-white text-xs font-medium">{bg.label}</span>
                          </div>
                          {customBackgroundUrl === bg.url && (
                            <div className="absolute top-1 right-1 bg-primary rounded-full p-0.5">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Custom Upload Mode */}
                  {backgroundMode === "custom" && (
                    <div>
                      {customBackgroundUrl ? (
                        <div className="relative">
                          <div
                            className="h-24 bg-cover bg-center rounded-lg"
                            style={{ backgroundImage: `url(${customBackgroundUrl})` }}
                          />
                          <Button
                            size="sm"
                            variant="destructive"
                            className="absolute top-2 right-2"
                            onClick={() => setCustomBackgroundUrl(null)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                          {uploadingBackground ? (
                            <>
                              <Loader2 className="w-6 h-6 text-primary animate-spin" />
                              <span className="text-xs text-muted-foreground">Upload...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-6 h-6 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Uploader un fond</span>
                            </>
                          )}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleBackgroundUpload}
                            disabled={uploadingBackground}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dish Dialog */}
      <Dialog open={showDishDialog} onOpenChange={setShowDishDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDish?.id ? "Modifier le plat" : "Nouveau plat"}
            </DialogTitle>
          </DialogHeader>
          
          {editingDish && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom du plat *</Label>
                  <Input
                    value={editingDish.nom}
                    onChange={(e) => setEditingDish({ ...editingDish, nom: e.target.value })}
                    placeholder="Ex: Pad Thaï"
                  />
                </div>
                <div>
                  <Label>Prix (€) *</Label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={editingDish.prix === 0 ? "" : editingDish.prix}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditingDish({ ...editingDish, prix: val === "" ? 0 : parseFloat(val) || 0 });
                    }}
                    placeholder="12.50"
                  />
                </div>
              </div>
              
              <div>
                <Label>Catégorie</Label>
                <Select
                  value={editingDish.categorie}
                  onValueChange={(v) => setEditingDish({ ...editingDish, categorie: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Description</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateDescription}
                    disabled={generatingDescription || !editingDish?.nom}
                    className="h-7 text-xs gap-1"
                  >
                    {generatingDescription ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Génération...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3 h-3" />
                        Générer avec IA
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  value={editingDish.description || ""}
                  onChange={(e) => setEditingDish({ ...editingDish, description: e.target.value })}
                  placeholder="Nouilles sautées aux crevettes, cacahuètes, germes de soja..."
                  rows={2}
                />
              </div>
              
              {/* Photo du plat */}
              <div>
                <Label className="mb-2 block">Photo du plat</Label>
                {editingDish.image_url ? (
                  <div className="relative">
                    <img
                      src={editingDish.image_url}
                      alt="Photo du plat"
                      className="w-full h-40 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setEditingDish({ ...editingDish, image_url: null })}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors">
                    {uploadingPhoto ? (
                      <>
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        <span className="text-sm text-muted-foreground">Upload en cours...</span>
                      </>
                    ) : (
                      <>
                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Cliquez pour ajouter une photo</span>
                        <span className="text-xs text-muted-foreground">(JPG, PNG - max 5MB)</span>
                      </>
                    )}
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={uploadingPhoto}
                    />
                  </label>
                )}
              </div>
              
              <div>
                <Label>Ingrédients (séparés par des virgules)</Label>
                <Input
                  value={editingDish.ingredientsText !== undefined 
                    ? editingDish.ingredientsText 
                    : (editingDish.ingredients || []).join(", ")}
                  onChange={(e) => setEditingDish({
                    ...editingDish,
                    ingredientsText: e.target.value
                  })}
                  placeholder="Nouilles de riz, crevettes, cacahuètes, soja..."
                />
              </div>
              
              <div>
                <Label>Accompagnements (séparés par des virgules)</Label>
                <Input
                  value={editingDish.accompagnementsText !== undefined 
                    ? editingDish.accompagnementsText 
                    : (editingDish.accompagnements || []).join(", ")}
                  onChange={(e) => setEditingDish({
                    ...editingDish,
                    accompagnementsText: e.target.value
                  })}
                  placeholder="Riz, frites, salade..."
                />
              </div>
              
              <div>
                <Label className="mb-2 block">Allergènes</Label>
                <div className="flex flex-wrap gap-2">
                  {ALLERGENES.map((allergene) => (
                    <Badge
                      key={allergene}
                      variant={editingDish.allergenes?.includes(allergene) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => {
                        const current = editingDish.allergenes || [];
                        if (current.includes(allergene)) {
                          setEditingDish({ ...editingDish, allergenes: current.filter(a => a !== allergene) });
                        } else {
                          setEditingDish({ ...editingDish, allergenes: [...current, allergene] });
                        }
                      }}
                    >
                      {allergene}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingDish.vegetarien}
                    onCheckedChange={(v) => setEditingDish({ ...editingDish, vegetarien: v })}
                  />
                  <Label className="flex items-center gap-1">
                    <Leaf className="w-4 h-4 text-green-600" />
                    Végétarien
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingDish.vegan}
                    onCheckedChange={(v) => setEditingDish({ ...editingDish, vegan: v })}
                  />
                  <Label className="flex items-center gap-1">
                    <Leaf className="w-4 h-4 text-green-700" />
                    Vegan
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingDish.sans_gluten}
                    onCheckedChange={(v) => setEditingDish({ ...editingDish, sans_gluten: v })}
                  />
                  <Label className="flex items-center gap-1">
                    <WheatOff className="w-4 h-4 text-amber-600" />
                    Sans gluten
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingDish.disponible}
                    onCheckedChange={(v) => setEditingDish({ ...editingDish, disponible: v })}
                  />
                  <Label>Disponible</Label>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDishDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveDish} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Formule Dialog */}
      <Dialog open={showFormuleDialog} onOpenChange={setShowFormuleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle Formule</DialogTitle>
          </DialogHeader>
          
          {editingFormule && (
            <div className="space-y-4">
              <div>
                <Label>Nom de la formule *</Label>
                <Input
                  value={editingFormule.nom}
                  onChange={(e) => setEditingFormule({ ...editingFormule, nom: e.target.value })}
                  placeholder="Menu Complet"
                />
              </div>
              
              <div>
                <Label>Prix (€) *</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={editingFormule.prix === 0 ? "" : editingFormule.prix}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditingFormule({ ...editingFormule, prix: val === "" ? 0 : parseFloat(val) || 0 });
                  }}
                  placeholder="25.00"
                />
              </div>
              
              <div>
                <Label className="mb-2 block">Composition</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={editingFormule.includes_entree}
                      onCheckedChange={(v) => setEditingFormule({ ...editingFormule, includes_entree: v })}
                    />
                    <Label>Entrée</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={editingFormule.includes_plat}
                      onCheckedChange={(v) => setEditingFormule({ ...editingFormule, includes_plat: v })}
                    />
                    <Label>Plat</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={editingFormule.includes_dessert}
                      onCheckedChange={(v) => setEditingFormule({ ...editingFormule, includes_dessert: v })}
                    />
                    <Label>Dessert</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={editingFormule.includes_boisson}
                      onCheckedChange={(v) => setEditingFormule({ ...editingFormule, includes_boisson: v })}
                    />
                    <Label>Boisson</Label>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFormuleDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveFormule}>
              <Check className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <div className="p-4 border-b flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <DialogTitle>Aperçu du Menu</DialogTitle>
            <div className="flex flex-wrap gap-2 items-center">
              {/* Orientation selector */}
              <Select value={pageOrientation} onValueChange={setPageOrientation}>
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="portrait">Portrait (A4)</SelectItem>
                  <SelectItem value="landscape">Paysage (A4)</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="sm" onClick={handleDownloadMenu}>
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">PNG</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportPDF}
                disabled={exportingPDF}
              >
                {exportingPDF ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-1" />
                )}
                <span className="hidden sm:inline">PDF</span>
              </Button>
              
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Imprimer</span>
              </Button>
              
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <ScrollArea className="h-[70vh]">
            {/* Classic Template - Using refactored component */}
            {generatedMenu && menuTemplate === "classic" && (
              <ClassicTemplate
                menuRef={menuRef}
                generatedMenu={generatedMenu}
                formules={formules}
                menuLanguage={menuLanguage}
                showPrix={showPrix}
                showAllergenes={showAllergenes}
              />
            )}
            
            {/* Thai Template - Using refactored component */}
            {generatedMenu && menuTemplate === "visual-thai" && (
              <ThaiTemplate
                menuRef={menuRef}
                generatedMenu={generatedMenu}
                menuLanguage={menuLanguage}
                showPrix={showPrix}
                customLogoUrl={customLogoUrl}
                pageOrientation={pageOrientation}
              />
            )}
            
            {/* Italian Template - Using refactored component */}
            {generatedMenu && menuTemplate === "visual-italian" && (
              <ItalianTemplate
                menuRef={menuRef}
                generatedMenu={generatedMenu}
                showPrix={showPrix}
                customLogoUrl={customLogoUrl}
                pageOrientation={pageOrientation}
              />
            )}
            
            {/* Mexican Template - Using refactored component */}
            {generatedMenu && menuTemplate === "visual-mexican" && (
              <MexicanTemplate
                menuRef={menuRef}
                generatedMenu={generatedMenu}
                showPrix={showPrix}
                customLogoUrl={customLogoUrl}
                pageOrientation={pageOrientation}
              />
            )}
            
            {/* Modern Template - Using refactored component */}
            {generatedMenu && menuTemplate === "visual-modern" && (
              <ModernTemplate
                menuRef={menuRef}
                generatedMenu={generatedMenu}
                showPrix={showPrix}
                customLogoUrl={customLogoUrl}
                pageOrientation={pageOrientation}
              />
            )}
          </ScrollArea>
          
          {/* Hidden printable menu for PDF export */}
          {generatedMenu && (
            <div 
              ref={printMenuRef}
              className="fixed left-[-9999px] top-0 bg-white"
              style={{
                width: pageOrientation === "landscape" ? "297mm" : "210mm",
                minHeight: pageOrientation === "landscape" ? "210mm" : "297mm",
                padding: "15mm",
                fontFamily: "Arial, sans-serif",
              }}
            >
              {/* Print-optimized Menu Content */}
              <div className="print-menu-content">
                {/* Header */}
                <div className="text-center mb-6 pb-4 border-b-2 border-gray-300">
                  <h1 className="text-2xl font-bold text-gray-800 mb-1">
                    {generatedMenu.titre}
                  </h1>
                  {generatedMenu.sous_titre && (
                    <p className="text-base text-gray-600 italic">
                      {generatedMenu.sous_titre}
                    </p>
                  )}
                </div>
                
                {/* Formules */}
                {(generatedMenu?.formules?.length > 0 || formules.length > 0) && (
                  <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
                    <h2 className="text-lg font-bold text-center mb-3 text-gray-800">
                      {menuLanguage === "en" ? "Set Menus" : "Nos Formules"}
                    </h2>
                    <div className="space-y-2">
                      {(generatedMenu?.formules || formules).map((formule, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <div>
                            <span className="font-semibold">{formule.nom}</span>
                            <span className="text-sm text-gray-600 ml-2">
                              ({formule.items.map(i => 
                                menuLanguage === "en" 
                                  ? (i === "entree" ? "Starter" : 
                                     i === "plat" ? "Main" : 
                                     i === "dessert" ? "Dessert" : "Drink")
                                  : (i === "entree" ? "Entrée" : 
                                     i === "plat" ? "Plat" : 
                                     i === "dessert" ? "Dessert" : "Boisson")
                              ).join(" + ")})
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {formule.original_prix && formule.original_prix !== formule.prix && (
                              <span className="text-sm text-gray-400 line-through">{formule.original_prix.toFixed(0)}€</span>
                            )}
                            <span className={`font-bold text-lg ${formule.original_prix && formule.original_prix !== formule.prix ? 'text-orange-600' : ''}`}>
                              {typeof formule.prix === 'number' ? formule.prix.toFixed(0) : formule.prix} €
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Menu sections in columns for landscape */}
                <div className={pageOrientation === "landscape" ? "grid grid-cols-2 gap-6" : "space-y-4"}>
                  {generatedMenu.sections?.map((section, sIdx) => (
                    <div key={sIdx} className="mb-4">
                      <h2 className="text-lg font-bold text-gray-800 mb-3 pb-1 border-b border-gray-300">
                        {menuLanguage === "en" 
                          ? (section.type === "entree" ? "Starters" :
                             section.type === "plat" ? "Main Courses" :
                             section.type === "dessert" ? "Desserts" : "Beverages")
                          : section.titre}
                      </h2>
                      <div className="space-y-2">
                        {section.plats?.map((plat, pIdx) => (
                          <div key={pIdx} className="flex justify-between items-start border-b border-dotted border-gray-200 pb-1">
                            <div className="flex-1 pr-2">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="font-medium text-sm">{plat.nom}</span>
                                {plat.vegetarien && <span className="text-xs text-green-600">(V)</span>}
                                {plat.vegan && <span className="text-xs text-green-700">(VG)</span>}
                                {plat.sans_gluten && <span className="text-xs text-amber-600">(SG)</span>}
                              </div>
                              {plat.description && (
                                <p className="text-xs text-gray-600">{plat.description}</p>
                              )}
                              {showAllergenes && plat.allergenes?.length > 0 && (
                                <p className="text-xs text-amber-700">
                                  Allergènes: {plat.allergenes.join(", ")}
                                </p>
                              )}
                            </div>
                            {showPrix && (
                              <div className="flex items-center gap-1 whitespace-nowrap">
                                {plat.original_prix && plat.original_prix !== plat.prix && (
                                  <span className="text-xs text-gray-400 line-through">{plat.original_prix.toFixed(2)} €</span>
                                )}
                                <span className={`font-bold text-sm ${plat.original_prix && plat.original_prix !== plat.prix ? 'text-orange-600' : ''}`}>
                                  {plat.prix.toFixed(2)} €
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Footer */}
                <div className="text-center mt-6 pt-4 border-t border-gray-300">
                  {/* Logo Food Truck United */}
                  <img 
                    src={FTU_LOGO_URL} 
                    alt="Food Truck United" 
                    className="h-10 mx-auto mb-2 opacity-70"
                  />
                  <p className="text-sm font-medium text-gray-800">
                    {generatedMenu.food_truck?.nom}
                  </p>
                  {showAllergenes && (
                    <p className="text-xs text-gray-600 mt-1">
                      {menuLanguage === "en" 
                        ? "Please inform us of any allergies. (V) Vegetarian, (VG) Vegan, (SG) Gluten-free"
                        : "Pour toutes allergies, merci de nous consulter. (V) Végétarien, (VG) Vegan, (SG) Sans gluten"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Importer des plats
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Preview */}
            {importData.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-muted px-4 py-2 font-medium text-sm">
                  Aperçu ({importData.length} plat{importData.length > 1 ? "s" : ""})
                </div>
                <div className="max-h-60 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-left">Nom</th>
                        <th className="px-3 py-2 text-left">Catégorie</th>
                        <th className="px-3 py-2 text-right">Prix</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importData.slice(0, 10).map((dish, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">{dish.nom}</td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className="text-xs">
                              {dish.categorie || "plat"}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right">{dish.prix}€</td>
                        </tr>
                      ))}
                      {importData.length > 10 && (
                        <tr className="border-t bg-muted/30">
                          <td colSpan={3} className="px-3 py-2 text-center text-muted-foreground">
                            ... et {importData.length - 10} autre(s) plat(s)
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Options */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="replace-all"
                checked={importReplaceAll}
                onCheckedChange={setImportReplaceAll}
              />
              <Label htmlFor="replace-all" className="text-sm cursor-pointer">
                Remplacer tous les plats existants
              </Label>
            </div>
            
            {importReplaceAll && (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  Attention : tous vos plats actuels seront supprimés !
                </span>
              </div>
            )}
            
            {/* Template download */}
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <span className="text-sm text-muted-foreground">
                Besoin d'un modèle de fichier ?
              </span>
              <Button variant="link" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-1" />
                Télécharger le modèle CSV
              </Button>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => {
              setShowImportDialog(false);
              setImportData([]);
              setImportReplaceAll(false);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleImportConfirm}
              disabled={importing || importData.length === 0}
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Importer {importData.length} plat{importData.length > 1 ? "s" : ""}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
